import {
	uniqWith,
	every,
	flatten,
	isEqual,
	some,
	find
} from "lodash";
import { REPORT_SCOPES } from "constants.js";
import {
	hasSectionCountRule,
	hasDetailCondition,
	isParentSectionCountRule
} from "./details-count-condition";
import {
	ENGINE_TYPES,
	TRIGGER_OPERATOR_CODES,
	TRIGGER_TYPES,
	UI_OPERATOR_TYPES
} from "../screens/rule-provider/constants.js";

class RuleConfigService {
	constructor($logger) {
		this.$logger = $logger;
	}

	reportScope(rule, fields) {
		if(hasSectionCountRule(rule)) {
			return hasDetailCondition(rule, fields) ? REPORT_SCOPES.reportAndDetails : REPORT_SCOPES.details;
		}
		const evaluatees = this.evaluatedFieldIds(rule);
		if(evaluatees.length === 0) {
			return REPORT_SCOPES.metadata;
		}

		const scopes = evaluatees.map(f => {
			const field = find(fields, m => m.Id == f);
			if(field.IsDetail)
				return REPORT_SCOPES.details;
			else
				return REPORT_SCOPES.report;
		});

		if(some(scopes, s => s == REPORT_SCOPES.report)) {
			if(some(scopes, s => s == REPORT_SCOPES.details))
				return REPORT_SCOPES.reportAndDetails;
			return REPORT_SCOPES.report;
		}

		if(this.isAggregate(rule)) {
			return REPORT_SCOPES.details;
		}

		return REPORT_SCOPES.detail;
	}

	hasAggregation(rule) {
		if(!rule.Children?.length) {
			return false;
		}

		if(rule.Aggregation !== undefined) {
			return true;
		}

		const hasChildAggregation = rule.Children.some(c => this.hasAggregation(c));

		return hasChildAggregation;
	}

	validationScope(rule, fields) {
		if(isParentSectionCountRule(rule, fields)) {
			return REPORT_SCOPES.details;
		}
		if(!some(rule.Triggers, t => !!t.Operator && t.Operator.Code === TRIGGER_OPERATOR_CODES.highlight)) {
			return rule.Scope.Code || this.reportScope(rule, fields).Code;
		}

		const highlightTriggers = rule.Triggers.filter(t => !!t.Operator && t.Operator.Code === TRIGGER_OPERATOR_CODES.highlight);
		const highlightTriggerFields = highlightTriggers.map(t => find(fields, f => f.Id === t.Field.Id));

		if(every(highlightTriggerFields, f => f.IsDetail)) {
			if(this.isAggregate(rule)) {
				return REPORT_SCOPES.details;
			}
			return REPORT_SCOPES.detail;
		}
		if(every(highlightTriggerFields, f => !f.IsDetail)) {
			return REPORT_SCOPES.report;
		}

		return REPORT_SCOPES.reportAndDetails;
	}

	getVisibilityRuleScope(rule, fields) {
		const visibilityTriggers = (rule.Triggers ?? []).filter(t => !!t.Operator && (t.Operator.Code === TRIGGER_OPERATOR_CODES.visibility || t.Operator.Code === TRIGGER_OPERATOR_CODES.readonly));

		if(!visibilityTriggers?.length) {
			return undefined;
		}
		if(isParentSectionCountRule(rule, fields)) {
			return REPORT_SCOPES.details;
		}

		const uxTriggerFields = visibilityTriggers
			.filter(t => t.Field?.Id !== undefined)
			.map(t => find(fields, f => f.Id === t.Field.Id));

		if(every(uxTriggerFields, f => f.IsDetail)) {
			if(this.isAggregate(rule)) {
				return REPORT_SCOPES.details;
			}
			return REPORT_SCOPES.detail;
		}
		if(every(uxTriggerFields, f => !f.IsDetail)) {
			return REPORT_SCOPES.report;
		}

		return REPORT_SCOPES.reportAndDetails;
	}

	evaluatedFieldIds(rule) {
		const conditionFieldIds = this.evaluatedConditionFieldIds(rule);
		const triggerFieldIds = this.evaluatedTriggerFieldIds(rule);

		return uniqWith(conditionFieldIds.concat(triggerFieldIds), isEqual);
	}

	evaluatedConditionFieldIds(rule) {
		if(!rule.Conditions?.length && !rule.Children?.length) {
			throw `There are no conditions or children associated with rule #${rule.id}.`;
		}

		if(rule.Conditions && rule.Conditions.length > 0) {
			return uniqWith(flatten(rule.Conditions.map(c => {
				const conditionFields = [];
				if(c.Field)
					conditionFields.push(c.Field.Id);
				if(c.CompareToField)
					conditionFields.push(c.CompareToField.Id);
				return conditionFields;
			})), isEqual);
		}
		else {
			return uniqWith(flatten(rule.Children.map((c) => this.evaluatedConditionFieldIds(c))), isEqual);
		}
	}

	evaluatedTriggerFieldIds(rule) {
		let triggerFieldIds = [];
		if(rule.Triggers && rule.Triggers.length > 0) {
			triggerFieldIds = rule.Triggers
				.filter(t => t.Field?.Id !== undefined)
				.map(t => t.Field.Id);
		}

		return triggerFieldIds;
	}

	isAggregate(rule) {
		if(rule.Children && rule.Children.length > 0) {
			for(let i = 0; i < rule.Children.length; i++) {
				const aggregated = this.isAggregate(rule.Children[i]);
				if(aggregated)
					return true;
			}
			return false;
		}

		if(rule.Engine != null) {
			return ENGINE_TYPES[rule.Engine.Code].isAggregate;
		}

		throw `Rule ${rule.Id} with unspecified children or conditions.`;
	}

	applyFunctionToAllChildRules(rule, updater) {
		if(rule.Children?.length) {
			for(const child of rule.Children) {
				this.applyFunctionToAllChildRules(child, updater);
				updater(child);
			}
		}

		updater(rule);
	}

	assignFieldOrders = (rule, fieldConfigs, conditionFieldIds) => {
		if(!!rule.Triggers?.length) {
			rule.Triggers
				.filter(t => t.Type?.Code === TRIGGER_TYPES.userInterface && t.Operator?.Code === UI_OPERATOR_TYPES.filter)
				.forEach(t => {
					t.order = fieldConfigs.find(f => f.Id === t.Field.Id)?.Order;
				});
		}

		if(!!conditionFieldIds?.length) {
			const conditionFields = conditionFieldIds
				.map(id => {
					const config = fieldConfigs.find(f => f.Id === id);

					return {
						fieldId: id, 
						order: config.Order
					};
				});

			rule.conditionFields = [...conditionFields].sort((a, b) => a.order < b.order ? -1 : 1);
		}
	};
}

export default angular
	.module("vesl.services.rule-config-service", [])
	.service("ruleConfigService", RuleConfigService).name;
